import React, { useEffect, useMemo, useState } from 'react';
import useApi from 'vl-common/src/hooks/useApi';
import { Button, Menu, MenuProps, Select, Spin } from 'antd';
import styled from '@emotion/styled';
import { LoadingOutlined } from 'antd/icons';
import { AiOutlineInfoCircle } from 'react-icons/ai';
import useMediaQuery from 'vl-common/src/hooks/useMediaQuery';
import CreateCase from '@components/Modal/CreateCase';
import { useQuery } from '@tanstack/react-query';
import * as actions from '@actions';
import { useDispatch } from 'react-redux';
import useAuth from '@vl-core/useAuth';
import { Case } from 'vl-common/src/schemas/shared/case';
import { infoModal } from 'vl-common/src/components/Modals';
import useIsNHS from '@hooks/useIsNHS';
import { useTranslation } from 'react-i18next';
import VirtualOnlyLabel from '@components/Patients/ToDos/VirtualOnlyLabel';

const StyledButton = styled(Button)`
  padding: 0;
  border: none;
  background: transparent !important;
  &.my-condition {
    display: inline-flex;
    align-items: center;
    vertical-align: middle;
    width: fit-content;
    & > span {
      margin-left: 10px;
    }
  }
`;

const CaseMenu = ({
  setSelectedCaseId,
  setNewCaseCreated,
  newCaseCreated,
  selectedCaseId,
  setSelectedCaseData,
  createNewCase,
  setCreateNewCase
}: {
  setSelectedCaseId: (value: string | null) => void;
  setNewCaseCreated: (value: string) => void;
  newCaseCreated: string;
  selectedCaseId?: string | null;
  setSelectedCaseData?: (value: Partial<Case>) => void;
  createNewCase?: boolean;
  setCreateNewCase?: (value: boolean) => void;
}) => {
  const { t } = useTranslation('common');
  const [current, setCurrent] = useState('caseMenu');
  const [reqAssessment, setReqAssessment] = useState(false);
  const isMobile = useMediaQuery('(max-width: 570px)');
  const isAXA = !useIsNHS();

  const dispatch = useDispatch();
  const { Api } = useApi();
  const { user } = useAuth();

  useEffect(() => {
    const newCaseModalRequested = !!new URLSearchParams(window.location.search).get('newcase');

    if (newCaseModalRequested) {
      setReqAssessment(true);
    }
  }, []);

  const { isLoading: isLoadingSurveyJsDefinition, data: { surveyjs_defs } = { surveyjs_defs: [] } } = useQuery({
    queryKey: ['useSurvey'],
    queryFn: () => dispatch(actions.getSurveyJsDefinition({ cat_code: 'UI', event_code: 'SR', current: 'true' }))
  });

  const {
    data: allCases = [],
    isLoading,
    refetch: refetchCases
  } = useQuery({
    queryKey: ['fetchAllCases'],
    queryFn: () =>
      Api.cases.getCases({
        order: 'desc',
        rows: 999,
        sort: 'todo'
      }),
    select: ({ status, cases }) => (status === 204 ? [] : cases || [])
  });
  const noCases = allCases.length === 0;
  const noSurveyDefinition = surveyjs_defs?.length > 0;

  useEffect(() => {
    if (isLoading || !allCases || noCases) return;

    const setCaseMenu = (selectedCaseId: string) => {
      const selectedCaseIndex = allCases.findIndex(
        ({ case_id: caseId }: { case_id: number }) => parseInt(selectedCaseId) === caseId
      );
      setSelectedCaseId(selectedCaseId);
      setSelectedCaseData?.(allCases[selectedCaseIndex]);
    };
    if (selectedCaseId && !newCaseCreated) {
      setCaseMenu(selectedCaseId);
    } else {
      setCaseMenu(allCases?.[0] ? `${allCases?.[0]?.case_id}` : '');
    }
  }, [setSelectedCaseId, isLoading, selectedCaseId, newCaseCreated, allCases, setSelectedCaseData]);

  const onClick: MenuProps['onClick'] = (e) => {
    if (e.key === 'myCondition') {
      infoModal({
        title: 'My Condition',
        closable: true,
        content: (
          <>
            <b>{t('What is a Condition?')}</b>
            <br />
            <p>
              {t(
                'A condition refers to any health issue or ailment within one of the specialties we offer assistance for. It could be a symptom, diagnosis, or medical concern related to a particular area of expertise.'
              )}
            </p>
            {isAXA && (
              <>
                <br />
                <b>{t('How to Get Help:')}</b>
                <br />
                <p>
                  {t(
                    'If you have a GP referral for a new condition, you can get help by clicking the &quot;new condition&quot; button, or &quot;get specialist opinion&quot; button on the homepage. Please make sure you discussed any new condition you&quot;re seeking help for with AXA to avoid extra costs.'
                  )}
                </p>
              </>
            )}
          </>
        ),
        width: 600
      });
    }
    if (e.key === 'newCondition') {
      setReqAssessment(true);
    }
    setCurrent(e.key);
  };

  const casesSubMenu = useMemo(() => {
    if (isLoading) {
      return [
        {
          label: <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />,
          value: 0
        }
      ];
    }

    if (noCases) {
      return [
        {
          label: 'My conditions',
          value: 0
        }
      ];
    }
    return allCases.map((singleCase: Case, index: number) => {
      if (singleCase?.condition === 'Dermatology') {
        return {
          label: `${singleCase?.condition} - Case ${singleCase?.client_case_id || singleCase?.case_id}`,
          value: `${singleCase?.case_id}`
        };
      }
      return {
        label: singleCase?.condition,
        value: `${singleCase?.case_id}`
      };
    });
  }, [allCases, isLoading, noCases]);

  const caseSelected = (value: string) => {
    setSelectedCaseId(value);
    sessionStorage.setItem('case-filter-selected', value);

    const caseIndex = allCases.findIndex((singleCase: Case) => `${singleCase.case_id}` === value);
    setSelectedCaseData?.(allCases[caseIndex]);
    if (newCaseCreated) setNewCaseCreated('');
  };
  const items: MenuProps['items'] = [
    {
      label: (
        <StyledButton
          type="text"
          icon={<AiOutlineInfoCircle aria-hidden style={{ verticalAlign: 'middle', fontSize: 20 }} />}
          className="my-condition"
          style={{ color: 'var(--productPrimary)' }}
          data-testid="my-condition"
        >
          {isMobile ? null : t('My condition')}
        </StyledButton>
      ),
      key: 'myCondition',
      className: 'case-menu-button'
    },
    {
      label: (
        <StyledButton type="text" style={{ fontWeight: 600 }} data-testid="new-condition">
          {t('Tell us about a new condition')}
        </StyledButton>
      ),
      key: 'newCondition',
      className: 'case-menu-button'
    },
    {
      label: (
        <span
          role="presentation"
          onKeyDown={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <Select
            onChange={caseSelected}
            value={selectedCaseId}
            options={casesSubMenu}
            popupMatchSelectWidth={false}
            styles={{
              popup: {
                root: {
                  minWidth: 'auto'
                }
              }
            }}
            data-testid="case-select"
          />
        </span>
      ),
      key: 'caseMenu',
      className: 'case-menu case-menu-button'
    },
    {
      label: (
        <span style={{ display: 'inline-flex', cursor: 'default' }}>
          <VirtualOnlyLabel caseId={Number(selectedCaseId)} />
        </span>
      ),
      disabled: true,
      key: 'virtualOnly',
      className: 'virtual-only-label'
    }
  ];

  const canSelfRefer = user?.self_refer && user?.insured;
  const filteredMenuItems = items
    .filter((item) => !(!canSelfRefer && item?.key === 'newCondition'))
    .filter((item) => item?.key !== 'newCondition' || noSurveyDefinition)
    .filter((item) => !noCases || item?.key !== 'caseMenu');

  const StyledMenu = styled(Menu)`
    border-bottom-color: transparent;
    min-width: min(100%, 530px);
    width: auto !important;
    overflow: visible !important;

    & > li.ant-menu-item::after {
      border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    }
    .case-menu-button {
      ::after {
        right: 0;
        left: 0;
      }
    }

    .virtual-only-label {
      cursor: default !important;
      background: transparent !important;
      padding-top: 6px !important;

      &::after {
        display: none !important;
        border-bottom: none !important;
      }

      &:hover,
      &.ant-menu-item-hover,
      &.ant-menu-item-selected,
      &.ant-menu-item-active {
        color: inherit !important;
        background: transparent !important;

        &::after {
          display: none !important;
        }
      }
    }

    .ant-menu-item {
      display: flex;

      padding-left: 5px !important;
      padding-right: 5px !important;
      align-items: center;
      justify-content: flex-start;
    }
    .ant-menu-item.case-menu {
      @media (max-width: 570px) {
      }
      white-space: nowrap !important;
      text-overflow: unset !important;
    }
    .ant-select-arrow {
      color: var(--productPrimary);
    }

    .ant-select-selector {
      border: none !important;
      @media (max-width: 570px) {
        max-width: 140px;
      }
    }
  `;
  return (
    <>
      <StyledMenu
        selectedKeys={[current]}
        mode="horizontal"
        items={filteredMenuItems}
        onClick={onClick}
        triggerSubMenuAction="click"
      />
      {(reqAssessment || createNewCase) && (
        <CreateCase
          open={reqAssessment || createNewCase}
          setOpen={(value: boolean) => {
            setReqAssessment(value);
            if (setCreateNewCase) {
              setCreateNewCase(value);
            }
          }}
          surveyJsDefinition={surveyjs_defs?.[0]}
          isLoadingSurveyJsDefinition={isLoadingSurveyJsDefinition}
          onSelfRefer={(case_id: number) => {
            refetchCases();
            setNewCaseCreated(`${case_id}`);
            sessionStorage.removeItem('case-filter-selected');
          }}
        />
      )}
    </>
  );
};

export default CaseMenu;
